import React from 'react';
import { ChatConversation } from '../hooks/types';
import { SidebarToolbar } from './SidebarToolbar';
import { NewChatModal } from './NewChatModal';
import { ConversationList } from './ConversationList';
import { useChatSidebarLogic } from '../hooks/useChatSidebarLogic';

interface ChatSidebarProps {
    conversations: ChatConversation[];
    activeConversationId: string | null;
    activeConnectionId?: string | null;
    onSelectConversation: (id: string, connectionId?: string | null) => void;
    onNewConversation: (number: string, name: string) => void;
    currentUserId?: string | null;
    isLoadingMore?: boolean;
    hasMoreConversations?: boolean;
    onLoadMore?: () => void;
    isRefreshing?: boolean;
    backendCounters?: { unassigned: number; mine: number; all: number };
    instanceName?: string;
    connectionId?: string | null;
    /** Escopo server-side da lista por canal (WA/IG). */
    onChannelConnectionChange?: (connectionId: string | null) => void;
}

/**
 * ChatSidebar — Sidebar de conversas do chat.
 * Responsabilidade: renderização da lista de conversas com filtros e busca.
 * O sync automático é gerenciado pelo hook useAutoSync no nível do ChatProvider.
 */
const ChatSidebar: React.FC<ChatSidebarProps> = ({
    conversations,
    activeConversationId,
    activeConnectionId = null,
    onSelectConversation,
    onNewConversation,
    currentUserId,
    isLoadingMore = false,
    hasMoreConversations = true,
    onLoadMore,
    isRefreshing = false,
    backendCounters,
    onChannelConnectionChange,
}) => {
    const {
        isNewChatModalOpen,
        setIsNewChatModalOpen,
        userRole,
        searchTerm,
        setSearchTerm,
        activeTab,
        setActiveTab,
        showArchived,
        showUnreadOnly,
        filteredConversations,
        handleArchiveToggle,
        handleUnreadToggle,
        assignedUserIds,
        setAssignedUserIds,
        channelConnectionId,
        setChannelConnectionId,
        crmUsers,
        counters,
        handleScroll,
        resolveConversationStageName,
        getAssignedUser,
        isFavorite
    } = useChatSidebarLogic({
        conversations,
        currentUserId,
        onLoadMore,
        isLoadingMore,
        hasMoreConversations,
        backendCounters
    });

    const handleChannelConnectionChange = (next: string | null) => {
        setChannelConnectionId(next);
        onChannelConnectionChange?.(next);
    };

    return (
        <div className="relative isolate z-20 flex h-full w-full min-w-0 flex-col gap-0 overflow-hidden">
            <SidebarToolbar
                activeTab={activeTab}
                onTabChange={setActiveTab}
                counters={counters}
                showArchived={showArchived}
                onArchiveToggle={handleArchiveToggle}
                showUnreadOnly={showUnreadOnly}
                onUnreadToggle={handleUnreadToggle}
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onNewChat={() => setIsNewChatModalOpen(true)}
                userRole={userRole}
                users={crmUsers}
                currentUserId={currentUserId}
                assignedUserIds={assignedUserIds}
                onAssignedUsersChange={setAssignedUserIds}
                channelConnectionId={channelConnectionId}
                onChannelConnectionChange={handleChannelConnectionChange}
            />

            <NewChatModal
                isOpen={isNewChatModalOpen}
                onClose={() => setIsNewChatModalOpen(false)}
                onSubmit={onNewConversation}
                conversations={conversations}
            />

            <ConversationList
                filteredConversations={filteredConversations}
                activeConversationId={activeConversationId}
                activeConnectionId={activeConnectionId}
                activeTab={activeTab}
                isRefreshing={isRefreshing}
                isLoadingMore={isLoadingMore}
                hasMoreConversations={hasMoreConversations}
                onScroll={handleScroll}
                onSelectConversation={onSelectConversation}
                resolveConversationStageName={resolveConversationStageName}
                getAssignedUser={getAssignedUser}
                isFavorite={isFavorite}
                showUnreadOnly={showUnreadOnly}
            />
        </div>
    );
};

export default ChatSidebar;
